/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { createSelector } from "@reduxjs/toolkit";
import { encode as base64Encode, decode } from "base-64";
import { AppState } from "../../";
import { toTimespanRangeMs } from "../../helpers/helpers";
import { IfilterObj } from "../types";
import { constStr } from "../../../globals/constants";
import { deviceSiteGroupSelector, deviceRegionFilterIdsSelector, deviceConfigStatusSelector } from "../Nodes/selectors";

const EMPTY_ARRAY: string[] = [];

interface IFilterObj {
  type: string;
  operator: string;
  fieldName: string;
  value: string | number;
  predicates: {}[];
}


export const alarmFilterSelector = (state: AppState) => state.alarmfilter;
export const alarmFilterSeveritySelector = (state: AppState) => state.alarmfilter.selectedEventSeverity;
export const alarmFilterManagedObjectSelector = (state: AppState) => state.alarmfilter.selectedManagedObject;
export const alarmFilterEventTypeSelector = (state: AppState) => state.alarmfilter.selectedEventType;
export const alarmFilterEventTimeSelector = (state: AppState) => state.alarmfilter.selectedEventTime;
export const alarmFilterEventFromTimeSelector = (state: AppState) => state.alarmfilter.selectedFromEventTime;
export const alarmFilterEventToTimeSelector = (state: AppState) => state.alarmfilter.selectedToEventTime;
export const alarmFilterHostNameSelector = (state: AppState) => state.alarmfilter.selectedHostName;
export const dataGridSortSelector = (state: AppState) => state.helpers.sortModel;
export const alarmFilterMaintenanceModeSelector = (state: AppState) => state.alarmfilter.selectedMaintenanceMode;
export const alarmFilterParentKeySelector = (state: AppState) => state.alarmfilter.parentKey;
const selectedGeoRegionSelector = (state: AppState) => state.admin.selectedGeoRegion.name || "";
const selectedGeoRegionIdsSelector = (state: AppState) => state.admin.selectedGeoRegionIds || EMPTY_ARRAY;
export const deviceKpiSyncSelector = (state: AppState) => state.filter.pmCollected;
export const deviceStatusSelector = (state: AppState) => state.filter.selectedDeviceStatus;
export const isDevicePageSelector = (state: AppState) => state.alarmfilter.isDeviceDetailPage;
export const alarmFilterLabelsSelector = (state: AppState) => state.alarmfilter.selectedLabels;
const currentPathnameSelector = () => window.location.pathname;
export const alarmFilterGeoRegionSelector = (state: AppState) => state.alarmfilter.geoRegion;
export const alarmFilterRegionIdsSelector = (state: AppState) => state.alarmfilter.regionFilterIds || [];

export const urlParameterAlarmsSelector = createSelector(
  deviceSiteGroupSelector,
  alarmFilterLabelsSelector,
  alarmFilterSeveritySelector,
  alarmFilterManagedObjectSelector,
  alarmFilterEventTypeSelector,
  alarmFilterEventTimeSelector,
  alarmFilterEventFromTimeSelector,
  alarmFilterEventToTimeSelector,
  alarmFilterHostNameSelector,
  alarmFilterMaintenanceModeSelector,
  alarmFilterParentKeySelector,
  selectedGeoRegionSelector,
  selectedGeoRegionIdsSelector,
  deviceKpiSyncSelector,
  deviceStatusSelector,
  deviceRegionFilterIdsSelector,
  deviceConfigStatusSelector,
  isDevicePageSelector,
  currentPathnameSelector,
  alarmFilterGeoRegionSelector,
  alarmFilterRegionIdsSelector,
  (_state: AppState, selectedGeoRegions?: string[] | undefined) => selectedGeoRegions,
  (siteGroup: string, labels: string[] | undefined, severity, managedObject, eventType, eventTime, eventFromTime, eventToTime, hostName, maintenanceMode, parentKey, geoRegionName, geoRegionIds, pmCollected, status, navRegionFilterIds, configstatus, isDeviceDetailPage, currentPathname, filterGeoRegion, regionFilterIds, selectedGeoRegions) => {
    const params = new URLSearchParams();
    const isDeviceDetail = isDeviceDetailPage;
    // Nav panel region selection takes highest priority, then fall back to alarm filter region, then header GeoRegionFilter
    const hasNavRegionFilter = !!(navRegionFilterIds && navRegionFilterIds.length > 0);
    const hasExplicitRegionFilter = !!(regionFilterIds && regionFilterIds.length > 0);
    const hasSelectedGeoRegions = Array.isArray(selectedGeoRegions) && selectedGeoRegions.length > 0;
    const effectiveRegionIds = hasNavRegionFilter ? navRegionFilterIds : (hasExplicitRegionFilter ? regionFilterIds : geoRegionIds);
    const isEnableFilter = severity || managedObject || eventType || eventTime || hostName || maintenanceMode || parentKey || geoRegionName || (effectiveRegionIds && effectiveRegionIds.length > 0) || isDeviceDetail || configstatus || filterGeoRegion || hasSelectedGeoRegions || hasExplicitRegionFilter;
    if (isEnableFilter) {
      const paramsString: IfilterObj = { type: "OR", operator: "", fieldName: "", value: "", predicates: [] };
      const andPredicate: IFilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };

      if (!isDeviceDetail && !!siteGroup && siteGroup !== constStr.ungrouped) {
        andPredicate.predicates.push({
          type: "",
          operator: "EQUAL",
          fieldName: "siteGroupId",
          value: siteGroup,
          predicates: [{}]
        });
      }
// **************************************************************************************************

      if (!isDeviceDetail && siteGroup === constStr.ungrouped) { andPredicate.predicates.push({ type: "", operator: "LIKE", fieldName: "siteGroupId", value: "99999999%", predicates: [{}] }); }

      // Region filtering logic - nav panel region filter takes highest priority
      if (!document.location.pathname.includes("device")) {
        if (currentPathname === "/") {
          // On homepage: check nav panel first, then alarm filter, then selected regions, then filterGeoRegion, then effective regions
          if (hasNavRegionFilter) {
            andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: navRegionFilterIds.join(","), predicates: [{}] });
          } else if (hasExplicitRegionFilter) {
            andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: regionFilterIds.join(","), predicates: [{}] });
          } else if (hasSelectedGeoRegions) {
            andPredicate.predicates.push({
              type: "",
              operator: "IN",
              fieldName: "geoRegionId",
              value: selectedGeoRegions!.join(","),
              predicates: [{}],
            });
          } else if (filterGeoRegion && (filterGeoRegion !== "all" && filterGeoRegion.objectKey !== "")) {
            andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "geoRegionName", value: filterGeoRegion.name, predicates: [{}] });
          } else if (effectiveRegionIds && effectiveRegionIds.length > 0 && siteGroup !== constStr.ungrouped) {
            andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: effectiveRegionIds.join(","), predicates: [{}] });
          }
        } else {
          // On other pages: check nav panel first, then alarm filter, then effective regions, then geoRegionName
          if (hasNavRegionFilter) {
            andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: navRegionFilterIds.join(","), predicates: [{}] });
          } else if (hasExplicitRegionFilter) {
            andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: regionFilterIds.join(","), predicates: [{}] });
          } else if (effectiveRegionIds && effectiveRegionIds.length > 0 && siteGroup !== constStr.ungrouped) {
            andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: effectiveRegionIds.join(","), predicates: [{}] });
          } else if (!!geoRegionName && siteGroup !== constStr.ungrouped) {
            andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "geoRegionName", value: geoRegionName, predicates: [{}] });
          }
        }
      }

// **************************************************************************************************

      if (severity) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "severity", value: severity, predicates: [{}] });
      }
      if (hostName) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "hostName", value: hostName, predicates: [{}] });
      }
      if (managedObject) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "managedObject", value: managedObject, predicates: [{}] });
      }

      if (labels && labels.length > 0) {
        andPredicate.predicates.push({ type: "", operator: "FIND_IN", fieldName: "labelNames", value: labels.join(","), predicates: [{}] });
      }

      if (eventType) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "eventType", value: eventType, predicates: [{}] });
      }

      if (eventTime) {
        if (eventTime === "Custom") {
          if (eventFromTime) {
            andPredicate.predicates.push({ type: "", operator: "GTE", fieldName: "occuredDateAndTimeMs", value: eventFromTime.toString() || "", predicates: [{}] });
          }

          if (eventToTime) {
            andPredicate.predicates.push({ type: "", operator: "LTE", fieldName: "occuredDateAndTimeMs", value: eventToTime.toString() || "", predicates: [{}] });
          }
        } else {
          const time = toTimespanRangeMs(eventTime);
          andPredicate.predicates.push({ type: "", operator: "GTE", fieldName: "occuredDateAndTimeMs", value: time?.from || "", predicates: [{}] });
        }
      }

      if (maintenanceMode) {
        andPredicate.predicates.push({ type: "", operator: "is_true", fieldName: "snMaintenanceMode", value: "", predicates: [{}] });
      }

      if (parentKey) {
        paramsString.predicates.push({ type: "", operator: "EQ", fieldName: "parentKey", value: parentKey, predicates: [{}] });
      }

      if (pmCollected) {
        andPredicate.predicates.push({ type: "", operator: "is_false", fieldName: "pmCollected", value: "", predicates: [{}] });
      }

      // Don't filter by device status on the detail page; alarms are already scoped by parentKey
      if (status && !isDeviceDetail) {
        if (status === "0" || status === 0) {
          andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "status", value: "19", predicates: [{}] });
        } else {
          andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "status", value: status, predicates: [{}] });
        }
      }
      if (configstatus && !isDeviceDetail) {
        andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "configurationStatus", value: configstatus, predicates: [{}] });
      }
      if (isDeviceDetail) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "parentKey", value: window.location.pathname?.split('/')[2], predicates: [{}] });
      }

      !isDeviceDetail && paramsString.predicates.push(andPredicate);

      // Include the "Server" OR branch for pure server alarms (those not tied to any device,
      // which have geoRegionId = NULL). Server-initiated alarms that ARE tied to a specific
      // device now have geoRegionId populated via the SQL view COALESCE, so they are already
      // caught by the main geoRegionId IN [...] branch above and correctly region-filtered.
      // The IS NULL guard prevents server alarms for devices in other regions from leaking
      // in when the user has selected a subset of regions.
      if (!isDeviceDetail && !hasNavRegionFilter) {
      const regionFilter: IfilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };
      regionFilter.predicates.push({ type: "", operator: "EQUAL", fieldName: "parentKey", value: "Server", predicates: [{}] });
      regionFilter.predicates.push({ type: "", operator: "is_null", fieldName: "geoRegionId", value: "", predicates: [{}] });

      if (severity) {
        regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "severity", value: severity, predicates: [{}] });
      }
      if (hostName) {
        regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "hostName", value: hostName, predicates: [{}] });
      }
      if (managedObject) {
        regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "managedObject", value: managedObject, predicates: [{}] });
      }

      if (labels && labels.length > 0) {
        regionFilter.predicates.push({ type: "", operator: "FIND_IN", fieldName: "labelNames", value: labels.join(","), predicates: [{}] });
      }

      if (eventType) {
        regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "eventType", value: eventType, predicates: [{}] });
      }
      if (eventTime) {
         if (eventTime === "Custom") {
          if (eventFromTime) {
            regionFilter.predicates.push({ type: "", operator: "GTE", fieldName: "occuredDateAndTimeMs", value: eventFromTime.toString() || "", predicates: [{}] });
          }

          if (eventToTime) {
            regionFilter.predicates.push({ type: "", operator: "LTE", fieldName: "occuredDateAndTimeMs", value: eventToTime.toString() || "", predicates: [{}] });
          }
        } else {
          const time = toTimespanRangeMs(eventTime);
          regionFilter.predicates.push({ type: "", operator: "GTE", fieldName: "occuredDateAndTimeMs", value: time?.from || "", predicates: [{}] });
        }
      }

      !isDeviceDetail && paramsString.predicates.push(regionFilter);
      }

      const base64EncodedStr = base64Encode(JSON.stringify(isDeviceDetail ? andPredicate : paramsString));
      if (isDeviceDetail) {
        if (andPredicate?.predicates.length > 0) {
          base64EncodedStr && params.append("filters", base64EncodedStr);
        }
      } else {
        if (paramsString?.predicates.length > 0) {
          paramsString && params.append("filters", base64EncodedStr);
        }
      }
      // console.log("Device Filter :>> ", decode(base64EncodedStr));
    }

    return params;
  }
);
